import SystemMsg from "./system-msg";
import Parameter from "./parameter";
import { getTimestamp } from "../utils";
import IVerifiedSender from "../interface/verified-sender";
import Global from "../global";
import ServiceName from "../enum/service-name";
import ParamStorage from "../enum/param-storage";
import ErrorCode from "../enum/error-code";
import ParameterStorage from "../class/parameter-storage";

class VerifiedSendersSingleton {

    public getAll(service: ServiceName | null = null): IVerifiedSender[] {
        if (service) {
            return this.getStore().getAll().filter((s: IVerifiedSender) => s.service === service);
        } else {
            return this.getStore().getAll();
        }
    }

    public async create(address: string, service: ServiceName): Promise<IVerifiedSender> {
        const item: IVerifiedSender | null = this.getAll().find((s: IVerifiedSender) => {
            return s.address === address && s.service === service;
        }) || null;
        if (item) {
            throw ErrorCode.AlreadyExist;
        } else {
            const sender: IVerifiedSender = await this.getStore().create({
                address,
                service,
                verified: false,
                timestamp: getTimestamp(),
                latestRequest: 0,
                requestCount: 0
            });
            await this.sendVerification(sender);
            return sender;
        }
    }

    public async verify(id: number): Promise<IVerifiedSender> {
        const sender: IVerifiedSender | null = this.getStore().get(id);
        if (sender && !sender.verified) {
            if (getTimestamp() - sender.latestRequest > this.getTimeout()) {
                if (sender.requestCount < Global.VERIFICATION_MAX_REQUEST) {
                    await this.sendVerification(sender);
                    return sender;
                } else {
                    throw ErrorCode.Expired;
                }
            } else {
                throw ErrorCode.Awaiting;
            }
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async delete(id: number): Promise<number> {
        const sender: IVerifiedSender | null = this.getStore().get(id);
        if (sender) {
            if (getTimestamp() - sender.latestRequest > this.getTimeout()) {
                return await this.getStore().delete(id);
            } else {
                throw ErrorCode.Awaiting;
            }
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async setVerified(address: string, service: ServiceName): Promise<IVerifiedSender> {
        const sender: IVerifiedSender | null = this.getAll().find((s: IVerifiedSender) => {
            return s.address === address && s.service === service;
        }) || null;
        if (sender) {
            sender.verified = true;
            return await this.getStore().update(sender);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public getTimeout(): number {
        return Global.VERIFICATION_TIMEOUT_VERIFY;
    }

    /* Private Public Methods */

    public async checkExpired(): Promise<void> {
        const expiredIds: number[] = this.getAll().filter((s: IVerifiedSender) => {
            return !s.verified && getTimestamp() - s.timestamp > Global.VERIFICATION_TIMEOUT_CREATE;
        }).map((s: IVerifiedSender) => s.id as number);
        if (expiredIds.length) {
            await this.getStore().bulk([], [], expiredIds);
        }
    }

    /* Private Methods */

    private async sendVerification(sender: IVerifiedSender): Promise<void> {
        sender.latestRequest = getTimestamp();
        sender.requestCount++;
        await this.getStore().update(sender);
        switch (sender.service) {
            case ServiceName.Email:
                await SystemMsg.emailAddressVerification(sender.address);
                break;
            case ServiceName.Sms:
                await SystemMsg.phoneAddressVerification(sender.address);
                break;
        }
    }

    /* Private Methods - Parameter Storage */

    private getStore(): ParameterStorage<IVerifiedSender> {
        return Parameter.get(ParamStorage.VerifiedSenders);
    }

}

const VerifiedSenders = new VerifiedSendersSingleton();

export default VerifiedSenders;